import React from "react";

import Switch from "antd/es/switch";
import cn from "classnames";
import { useIntl } from "react-intl";

import ActivityType from "@mapmycustomers/shared/types/entity/activities/ActivityType";
import { Labeled, NumberField } from "@mapmycustomers/ui";

import MultiSelectActivityTypeField from "@app/component/input/MultiSelectActivityTypeField";
import TextWithInfo from "@app/component/typography/TextWithInfo";

import styles from "./CadenceFields.module.scss";

interface Props {
  cadence?: false | number;
  cadenceActivityTypeIds?: ActivityType["id"][];
  onCadenceActivityTypesChange?: (activityTypeIds: ActivityType["id"][]) => void;
  onCadenceChange?: (value: false | number) => void;
}

const CadenceFields = ({
  cadence,
  cadenceActivityTypeIds,
  onCadenceActivityTypesChange,
  onCadenceChange,
}: Props) => {
  const intl = useIntl();

  return (
    <>
      <Labeled
        label={
          <TextWithInfo
            info={intl.formatMessage({
              id: "createGroupModal.field.cadence.tooltip",
              defaultMessage:
                'By setting a frequency, records in this group will show as "Overdue" if activities are not done in the selected interval',
              description: "Cadence field tooltip in the Create Group modal",
            })}
          >
            {intl.formatMessage({
              id: "createGroupModal.field.cadenceSwitch",
              defaultMessage: "Set Frequency",
              description: "Cadence field switch label in the Create Group modal",
            })}
          </TextWithInfo>
        }
        labelPosition="side"
        sideLabelSpan={20}
      >
        <Switch
          checked={cadence !== false}
          onChange={(value) => {
            onCadenceChange?.(value && 1); // return 1 if true, but return false as is
          }}
        />
      </Labeled>

      <NumberField
        className={cn({ [styles.hidden]: cadence === false })}
        label={intl.formatMessage({
          id: "createGroupModal.field.cadence",
          defaultMessage: "Frequency Interval (in days)",
          description: "Cadence field label in the Create Group modal",
        })}
        max={500}
        onChange={(value) => onCadenceChange?.(value ?? 0)}
        value={(cadence as number) ?? undefined}
      />

      <MultiSelectActivityTypeField
        allowClear
        className={cn({ [styles.hidden]: !cadence })}
        label={intl.formatMessage({
          id: "createGroupModal.field.activityTypes",
          defaultMessage: "Accepted activity types",
          description: "Activity types selector in the Create Group modal",
        })}
        onChange={onCadenceActivityTypesChange}
        placeholder={
          cadenceActivityTypeIds?.length
            ? undefined
            : intl.formatMessage({
                id: "createGroupModal.field.activityTypes.placeholder",
                defaultMessage: "All types",
                description: "Activity types placeholder in the Create Group modal",
              })
        }
        popupClassName={styles.activityTypeDropdown}
        value={cadenceActivityTypeIds}
      />
    </>
  );
};

export default CadenceFields;
